/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import FolderIcon from "@mui/icons-material/Folder";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useSnackbar } from "notistack";
import * as teamsApi from "../../../api/teams";
import * as drive from "../../../api/drive";
import { NamePromptDialog } from "./components/DriveDialogs";
import TeamMembersDialog from "./components/TeamMembersDialog";
import { formatDate } from "./format";

/**
 * A team's top-level folders. Navigating into one uses the ordinary
 * /drive/folders/:id route — once you're inside, a team folder behaves like any
 * other folder you have access to, because access resolution already covers it.
 */
const TeamPage = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState({ loading: true, role: null, teamName: null, folders: [], error: null });
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { role, teamName, folders } = await teamsApi.getTeam(teamId);
      setState({ loading: false, role, teamName, folders, error: null });
    } catch (err) {
      setState({ loading: false, role: null, teamName: null, folders: [], error: err.message || "Team not found." });
    }
  }, [teamId]);

  useEffect(() => {
    load();
  }, [load]);

  const createFolder = async (folderName) => {
    await drive.createFolder({ folderName, parentFolderId: null, teamId });
    enqueueSnackbar("Folder created.", { variant: "success" });
    load();
  };

  const rename = async (teamName) => {
    await teamsApi.updateTeam(teamId, { teamName });
    enqueueSnackbar("Team renamed.", { variant: "success" });
    load();
    window.dispatchEvent(new Event("vcs:teams-changed"));
  };

  if (state.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "accent.main" }} />
      </Box>
    );
  }

  if (state.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{state.error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <GroupOutlinedIcon sx={{ color: "accent.main" }} />
          <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{state.teamName || "Team folder"}</Typography>
          <Chip size="small" label={state.role} sx={{ fontSize: 11, textTransform: "capitalize" }} />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {state.role === "admin" && (
            <Button
              startIcon={<EditOutlinedIcon />}
              sx={{ color: "text.color", textTransform: "none" }}
              onClick={() => setDialog({ kind: "rename" })}
            >
              Rename
            </Button>
          )}
          <Button
            startIcon={<GroupOutlinedIcon />}
            sx={{ color: "text.color", textTransform: "none" }}
            onClick={() => setDialog({ kind: "members" })}
          >
            Members
          </Button>
          <Button
            variant="contained"
            startIcon={<CreateNewFolderIcon />}
            sx={{ backgroundColor: "accent.solid", color: "#fff", textTransform: "none" }}
            onClick={() => setDialog({ kind: "create-folder" })}
          >
            New folder
          </Button>
        </Box>
      </Box>

      <Paper variant="outlined">
        {state.folders.length === 0 ? (
          <Typography sx={{ p: 4, textAlign: "center", fontSize: 13.5, color: "secondaryText.color" }}>
            No folders in this team yet. Anything you create here belongs to the
            team, not to you — it stays put if you leave.
          </Typography>
        ) : (
          state.folders.map((folder, index) => (
            <Box
              key={folder.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/teams/${teamId}/folders/${folder.id}`)}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") &&
                navigate(`/teams/${teamId}/folders/${folder.id}`)
              }
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 2,
                py: 1.5,
                cursor: "pointer",
                borderTop: index === 0 ? "none" : "1px solid",
                borderColor: "border.color",
                "&:hover": { backgroundColor: "tertiary.main" },
              }}
            >
              <FolderIcon fontSize="small" sx={{ color: "accent.main" }} />
              <Typography sx={{ fontSize: 13.5, flex: 1 }}>{folder.folder_name}</Typography>
              <Typography sx={{ fontSize: 12, color: "secondaryText.color" }}>
                {formatDate(folder.updated_at)}
              </Typography>
            </Box>
          ))
        )}
      </Paper>

      <NamePromptDialog
        open={dialog?.kind === "create-folder"}
        title="New team folder"
        label="Folder name"
        confirmLabel="Create"
        onConfirm={createFolder}
        onClose={() => setDialog(null)}
      />
      <NamePromptDialog
        open={dialog?.kind === "rename"}
        title="Rename team"
        label="Team name"
        confirmLabel="Save"
        initialValue={state.teamName || ""}
        onConfirm={rename}
        onClose={() => setDialog(null)}
      />
      <TeamMembersDialog
        open={dialog?.kind === "members"}
        variant="team"
        teamId={teamId}
        canManage={state.role === "admin"}
        onClose={() => setDialog(null)}
      />
    </Box>
  );
};

export default TeamPage;
